import { toast } from '@systemeio/ui'
import { useOfferActions } from 'modules/digital-product/hooks/use-offer-actions'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { StoreId } from 'modules/stores/types/stores-interface'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { handle422Error } from 'shared/utils/parse-422-errors'
import { useItemProductActions } from '../../hooks/use-item-product-actions'
import {
  PhysicalProductItemEditType,
  PhysicalProductItemErrorsType,
  PhysicalProductItemViewType,
} from '../../types/product-interface'
import { getItemUpdateData } from '../../utils/get-item-update-data'
import { defaultErrors } from '../constants/default-errors'
import { useErrorMessageInTabs } from './use-error-message-in-tabs'

interface Props {
  tempState: PhysicalProductItemEditType
  setTempState: Dispatch<SetStateAction<PhysicalProductItemEditType>>
  storeId: StoreId
  setErrors: Dispatch<SetStateAction<PhysicalProductItemErrorsType>>
  initialProductId?: number
}

export const useSavePhysicalProductItem = ({
  tempState,
  setTempState,
  storeId,
  setErrors,
  initialProductId,
}: Props) => {
  const { t } = useLocoTranslation()

  const [isFetchingPublish, setIsFetchingPublish] = useState(false)
  const [isFetchingDraft, setIsFetchingDraft] = useState(false)

  const physicalProductId = useRef(tempState.checkoutPage.physicalProductId)
  const offerId = useRef(tempState.checkoutPage.offerId)

  useEffect(() => {
    physicalProductId.current = tempState.checkoutPage.physicalProductId
  }, [tempState.checkoutPage.physicalProductId])

  const { mutate: mutateStore } = useStore(storeId)

  const { createPhysicalProduct, updatePhysicalProduct } = useItemProductActions()

  const { redirectToMainPage } = useStoreQuery()

  const { createOffer, updateOffer } = useOfferActions()

  const { formErrorMessage } = useErrorMessageInTabs()

  const onSave = async (item: PhysicalProductItemEditType) => {
    try {
      setErrors(defaultErrors)

      if (!offerId.current) {
        const offer = await createOffer({ product: physicalProductId.current })
        offerId.current = offer.id

        if (!offerId.current) {
          toast.error(t('global.error'))
          throw new Error()
        }
      } else if (physicalProductId.current !== initialProductId) {
        await updateOffer(offerId.current, { product: physicalProductId.current })
      }

      const mutateProductItemData = async (responseData: PhysicalProductItemViewType) =>
        await mutateStore(
          data =>
            data && {
              ...data,
              items: item.id
                ? data.items.map(item => (item.id === responseData.id ? responseData : item))
                : [...data.items, responseData],
            },
          false,
        )

      if (item.id) {
        const responseData = await updatePhysicalProduct(
          item.id,
          getItemUpdateData({
            item,
            offerId: offerId.current,
            physicalProductId: physicalProductId.current,
          }),
        )
        await mutateProductItemData(responseData)
      } else {
        const responseData = await createPhysicalProduct(
          storeId,
          getItemUpdateData({
            item,
            offerId: offerId.current,
          }),
        )

        physicalProductId.current = responseData.id

        await mutateProductItemData(responseData)
      }

      toast.success(t('global.changes_saved'))
      await redirectToMainPage()
    } catch (e) {
      const newErrors = handle422Error<PhysicalProductItemErrorsType>(e, setErrors)

      const { errorMessage } = formErrorMessage({ errors: { ...defaultErrors, ...newErrors } })

      errorMessage && toast.error(errorMessage)
    } finally {
      setIsFetchingPublish(false)
      setIsFetchingDraft(false)
    }
  }

  const onPublish = async () => {
    const item = { ...tempState, draft: false }
    setIsFetchingPublish(true)
    setTempState(item)
    await onSave(item)
  }

  const onSaveDraft = async () => {
    const item = { ...tempState, draft: true }
    setIsFetchingDraft(true)
    setTempState(item)
    await onSave(item)
  }

  return { onPublish, isFetchingPublish, isFetchingDraft, onSaveDraft }
}
